import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import {
  Button,
  CircularProgress,
  ButtonBase,
  Typography
} from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { CardExpansionPanel } from '~/modules/common/components';
import { useNotReferencedProjectRoles } from '../hooks';
import { Title } from './Title';
import {
  useCardExpansionPanelStyles,
  useNotReferencedProjectRolesStyles
} from './styles';
import { NotReferencedProjectRoleList } from './NotReferencedProjectRoleList';

const expansionPanelSummaryProps = { role: 'alert' };

export const NotReferencedProjectRoles = ({
  projectId,
  canAddRole,
  configuredProjectRoles,
  onAddRole,
  showMissingRoles: showMissingRolesOverrides = false
}) => {
  const [showMissingRoles, setShowMissingRoles] = useState(
    showMissingRolesOverrides
  );
  const cardExpansionPanelClasses = useCardExpansionPanelStyles();
  const classes = useNotReferencedProjectRolesStyles();

  const onComplete = useCallback(() => {
    setShowMissingRoles(true);
  }, []);

  const {
    fetchNotReferencedProjectRoles,
    isLoading,
    roles,
    notFetchedRolesCount
  } = useNotReferencedProjectRoles({ projectId, onComplete });

  useEffect(() => {
    if (showMissingRolesOverrides) {
      fetchNotReferencedProjectRoles();
    }
  }, [fetchNotReferencedProjectRoles, showMissingRolesOverrides]);

  const configuredProjectRolesSet = useMemo(
    () => new Set(configuredProjectRoles),
    [configuredProjectRoles]
  );

  const filteredRoles = roles.filter(r => !configuredProjectRolesSet.has(r.id));

  const titleComponentProps = useMemo(
    () => ({
      roleCount: notFetchedRolesCount + filteredRoles.length
    }),
    [filteredRoles.length, notFetchedRolesCount]
  );

  const expansionPanelSummaryWrapperProps = useMemo(
    () => ({
      className: classes.expansionPanelSummaryWrapper
    }),
    [classes.expansionPanelSummaryWrapper]
  );

  if (!showMissingRoles || isLoading)
    return (
      <Button
        disabled={isLoading}
        onClick={fetchNotReferencedProjectRoles}
        variant="outlined"
      >
        <>
          {isLoading && (
            <CircularProgress className={classes.circularProgress} size={16} />
          )}
          <FormattedMessage
            id={
              isLoading
                ? 'advancedRateCard.notReferencedProjectRoles.checkingForMissingRoles'
                : 'advancedRateCard.notReferencedProjectRoles.checkForMissingRoles'
            }
          />
        </>
      </Button>
    );

  return (
    <>
      {notFetchedRolesCount + filteredRoles.length > 0 ? (
        <CardExpansionPanel
          TitleComponent={Title}
          titleComponentProps={titleComponentProps}
          ExpansionPanelSummaryWrapperComponent={ButtonBase}
          expansionPanelSummaryWrapperProps={expansionPanelSummaryWrapperProps}
          expansionPanelSummaryProps={expansionPanelSummaryProps}
          defaultExpanded={false}
          classes={cardExpansionPanelClasses}
          dataQeId="NotReferencedProjectRoles"
        >
          <NotReferencedProjectRoleList
            roles={filteredRoles}
            canAddRole={canAddRole}
            onAddClick={onAddRole}
            notFetchedRolesCount={notFetchedRolesCount}
          />
        </CardExpansionPanel>
      ) : (
        <Alert className={classes.noMissingRolesAlert}>
          <AlertTitle className={classes.noMissingRolesTitle}>
            <Typography variant="body2">
              <FormattedMessage id="advancedRateCard.notReferencedProjectRoles.noMissingRolesFound" />
            </Typography>
          </AlertTitle>
        </Alert>
      )}
    </>
  );
};

NotReferencedProjectRoles.propTypes = {
  projectId: PropTypes.string,
  canAddRole: PropTypes.bool,
  configuredProjectRoles: PropTypes.array,
  onAddRole: PropTypes.func,
  showMissingRoles: PropTypes.bool
};
